/**
 * Email Templates API endpoints — `/workspaces/:workspaceId/email-templates`.
 *
 * A reusable HTML email template — promoted from a sent campaign, imported
 * from a file, or authored in Studio (Phase 1 build). `@xavior/contracts` has
 * no flat `EmailTemplate` export, so the types are aliased off `components`
 * here, mirroring endpoints/entitlements.ts.
 */

import { http } from '@/lib/http'
import type { components } from '@xavior/contracts'

export type EmailTemplate = components['schemas']['EmailTemplate']
export type EmailTemplateCreate = components['schemas']['EmailTemplateCreate']
export type EmailTemplateUpdate = components['schemas']['EmailTemplateUpdate']

/** GET /workspaces/:workspaceId/email-templates — all templates for the workspace. */
export async function listEmailTemplates(workspaceId: string): Promise<EmailTemplate[]> {
  const response = await http.get<{ items: EmailTemplate[] }>(
    `/workspaces/${workspaceId}/email-templates`,
  )
  return response.data.items
}

/** GET /workspaces/:workspaceId/email-templates/:templateId — one template. */
export async function getEmailTemplate(
  workspaceId: string,
  templateId: string,
): Promise<EmailTemplate> {
  const response = await http.get<EmailTemplate>(
    `/workspaces/${workspaceId}/email-templates/${templateId}`,
  )
  return response.data
}

/** POST /workspaces/:workspaceId/email-templates — create a template. */
export async function createEmailTemplate(
  workspaceId: string,
  body: EmailTemplateCreate,
): Promise<EmailTemplate> {
  const response = await http.post<EmailTemplate>(
    `/workspaces/${workspaceId}/email-templates`,
    body,
  )
  return response.data
}

/** PATCH /workspaces/:workspaceId/email-templates/:templateId — partial update. */
export async function updateEmailTemplate(
  workspaceId: string,
  templateId: string,
  patch: EmailTemplateUpdate,
): Promise<EmailTemplate> {
  const response = await http.patch<EmailTemplate>(
    `/workspaces/${workspaceId}/email-templates/${templateId}`,
    patch,
  )
  return response.data
}

/** DELETE /workspaces/:workspaceId/email-templates/:templateId — 204, no body. */
export async function deleteEmailTemplate(
  workspaceId: string,
  templateId: string,
): Promise<void> {
  await http.delete(`/workspaces/${workspaceId}/email-templates/${templateId}`)
}
